import { Artifact, ArtifactClassification } from '@/gen/artifacts/v1/model_pb'

type DownloadableArtifact = Pick<Artifact, 'artifact' | 'classification'>

/**
 * Classification says which RPC resolves the id: PRIVATE is an org document,
 * PUBLIC an ArtifactRevision. UNSPECIFIED fails the gate -- some builders omit
 * classification, and guessing would route the id to an RPC it may not resolve
 * against, failing a whole bundle.
 */
export const isDownloadableArtifact = (artifact: DownloadableArtifact): boolean =>
  artifact.artifact.case === 'artifactId' &&
  (artifact.classification === ArtifactClassification.PRIVATE ||
    artifact.classification === ArtifactClassification.PUBLIC)

/** Ids to send to DownloadArtifactBundle, split by where they resolve. */
export type BundleSelection = {
  artifactIds: string[]
  revisionIds: string[]
}

export const toBundleSelection = (artifacts: DownloadableArtifact[]): BundleSelection => {
  const selection: BundleSelection = { artifactIds: [], revisionIds: [] }

  for (const artifact of artifacts) {
    if (!isDownloadableArtifact(artifact)) {
      continue
    }

    // The oneof, not artifact.id: for a public artifact `id` is the Artifact
    // row, while the oneof carries the revision the bucket key is on.
    const id = artifact.artifact.value as string
    if (artifact.classification === ArtifactClassification.PUBLIC) {
      selection.revisionIds.push(id)
    } else {
      selection.artifactIds.push(id)
    }
  }

  return selection
}

/** How many of these artifacts the bundle can actually include. */
export const bundleableCount = (artifacts: DownloadableArtifact[]): number => {
  const { artifactIds, revisionIds } = toBundleSelection(artifacts)

  return artifactIds.length + revisionIds.length
}
